import React, { useState } from 'react';
import {
  TrendingUp,
  Gift,
  Ticket,
  Clock,
  Share2,
  Settings,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MobileHeader, MobileBottomNav } from '@/components/layout/Navigation';
import {
  BalanceCard,
  QuickAction,
  EarnMethodCard,
  StreakCard,
  ChallengeCard,
  PartnerOfferCard,
  InternalRedemptionCard,
  TransactionRow,
  VoucherCard,
  ReferralCard,
} from '@/components/cards/LoyaltyCards';
import { MiniEarnRedeemChart } from '@/components/charts/LoyaltyCharts';
import {
  userWallet,
  monthlyPointsData,
  earnMethods,
  streakRewards,
  activeChallenges,
  internalRedemptions,
  partnerOffers,
  partnerCategories,
  transactions,
  userVouchers,
  referralData,
} from '@/data/mockData';
import { useApp } from '@/context/AppContext';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// Customer Home Page
export function CustomerHome() {
  const { setActiveTab, addNotification } = useApp();

  return (
    <div className="pb-24">
      <MobileHeader title="Loyalty Wallet" />
      <div className="p-4 space-y-6">
        {/* Balance Card */}
        <BalanceCard
          balance={userWallet.availablePoints}
          pending={userWallet.pendingPoints}
          expiring={userWallet.expiringPoints}
          daysToExpiry={userWallet.daysToExpiry}
        />

        {/* Quick Actions */}
        <div className="grid grid-cols-4 gap-3">
          <QuickAction
            icon={TrendingUp}
            label="Earn"
            variant="success"
            onClick={() => setActiveTab('earn')}
          />
          <QuickAction
            icon={Gift}
            label="Redeem"
            variant="primary"
            onClick={() => setActiveTab('redeem')}
          />
          <QuickAction
            icon={Ticket}
            label="Vouchers"
            variant="default"
            onClick={() => setActiveTab('vouchers')}
          />
          <QuickAction
            icon={Clock}
            label="History"
            variant="default"
            onClick={() => setActiveTab('history')}
          />
        </div>

        {/* Monthly Activity */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              Monthly Activity
              <Button variant="ghost" size="sm" className="text-xs">
                View All <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <MiniEarnRedeemChart data={monthlyPointsData} />
          </CardContent>
        </Card>

        {/* Active Challenges */}
        <div>
          <h2 className="font-display font-semibold text-lg mb-3">Active Challenges</h2>
          <div className="space-y-3">
            {activeChallenges.map((challenge) => (
              <ChallengeCard
                key={challenge.id}
                challenge={challenge}
                onStart={() => addNotification({ type: 'success', message: 'Challenge started!' })}
              />
            ))}
          </div>
        </div>

        {/* Referral Section */}
        <ReferralCard
          referralData={referralData}
          onShare={() => {
            navigator.clipboard?.writeText(`Join Physiomobile with my code: ${referralData.code}`);
            addNotification({ type: 'success', message: 'Referral link copied!' });
          }}
        />

        {/* Settings Link */}
        <Card className="cursor-pointer card-interactive" onClick={() => addNotification({ type: 'info', message: 'Settings coming soon' })}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <Settings className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium">Wallet Settings</p>
                  <p className="text-sm text-muted-foreground">Notifications & preferences</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>
      <MobileBottomNav />
    </div>
  );
}

// Earn Points Page
export function CustomerEarn() {
  const { addNotification } = useApp();

  return (
    <div className="pb-24">
      <MobileHeader title="Earn Points" />
      <div className="p-4 space-y-6">
        {/* Streak Card */}
        <StreakCard currentStreak={userWallet.streakDays} rewards={streakRewards} />

        {/* Earn Methods */}
        <div>
          <h2 className="font-display font-semibold text-lg mb-3">Ways to Earn</h2>
          <div className="space-y-3">
            {earnMethods.map((method) => (
              <EarnMethodCard
                key={method.id}
                method={method}
                onClick={() => addNotification({ type: 'info', message: `Learn more about ${method.title}` })}
              />
            ))}
          </div>
        </div>

        {/* Challenges */}
        <div>
          <h2 className="font-display font-semibold text-lg mb-3">Challenges & Campaigns</h2>
          <div className="space-y-3">
            {activeChallenges.map((challenge) => (
              <ChallengeCard
                key={challenge.id}
                challenge={challenge}
                onStart={() => addNotification({ type: 'success', message: 'Challenge details opened!' })}
              />
            ))}
          </div>
        </div>

        {/* Referral */}
        <ReferralCard
          referralData={referralData}
          onShare={() => {
            navigator.clipboard?.writeText(`Join Physiomobile with my code: ${referralData.code}`);
            addNotification({ type: 'success', message: 'Referral link copied!' });
          }}
        />
      </div>
      <MobileBottomNav />
    </div>
  );
}

// Redeem Page
export function CustomerRedeem() {
  const { addNotification } = useApp();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showOfferDetail, setShowOfferDetail] = useState(null);

  const filteredOffers =
    selectedCategory === 'all'
      ? partnerOffers
      : partnerOffers.filter((o) => o.category === selectedCategory);

  if (showOfferDetail) {
    return (
      <OfferDetailView
        offer={showOfferDetail}
        onBack={() => setShowOfferDetail(null)}
        onRedeem={() => {
          addNotification({ type: 'success', message: 'Voucher redeemed successfully!' });
          setShowOfferDetail(null);
        }}
      />
    );
  }

  return (
    <div className="pb-24">
      <MobileHeader title="Redeem Points" />
      <div className="p-4 space-y-4">
        {/* Points Balance Mini */}
        <div className="flex items-center justify-between bg-primary/5 rounded-xl p-4">
          <div>
            <p className="text-sm text-muted-foreground">Available Points</p>
            <p className="text-2xl font-display font-bold text-primary">
              {userWallet.availablePoints.toLocaleString()}
            </p>
          </div>
          <Badge variant="premium">Gold Member</Badge>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="physiomobile" className="w-full">
          <TabsList className="w-full">
            <TabsTrigger value="physiomobile" className="flex-1">
              Physiomobile
            </TabsTrigger>
            <TabsTrigger value="partners" className="flex-1">
              Partners
            </TabsTrigger>
          </TabsList>

          {/* Physiomobile Tab */}
          <TabsContent value="physiomobile" className="mt-4 space-y-3">
            {internalRedemptions.map((item) => (
              <InternalRedemptionCard
                key={item.id}
                item={item}
                onRedeem={() => addNotification({ type: 'success', message: `${item.title} redeemed!` })}
              />
            ))}
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners" className="mt-4 space-y-4">
            {/* Category Filters */}
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 custom-scrollbar">
              <Button
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('all')}
                className="shrink-0"
              >
                All
              </Button>
              {partnerCategories.map((cat) => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.id)}
                  className="shrink-0"
                >
                  {cat.name}
                </Button>
              ))}
            </div>

            {/* Partner Offers */}
            <div className="space-y-3">
              {filteredOffers.map((offer) => (
                <PartnerOfferCard
                  key={offer.id}
                  offer={offer}
                  onClick={() => setShowOfferDetail(offer)}
                />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <MobileBottomNav />
    </div>
  );
}

// Offer Detail View
function OfferDetailView({ offer, onBack, onRedeem }) {
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [showVoucher, setShowVoucher] = useState(false);

  const handleRedeem = () => {
    setIsRedeeming(true);
    setTimeout(() => {
      setIsRedeeming(false);
      setShowVoucher(true);
    }, 1500);
  };

  if (showVoucher) {
    return (
      <div className="min-h-screen bg-background">
        <MobileHeader title="Your Voucher" showBack onBack={onBack} />
        <div className="p-4 space-y-6">
          <Card className="text-center">
            <CardContent className="p-6">
              <div className="w-16 h-16 mx-auto bg-success/10 rounded-full flex items-center justify-center mb-4">
                <Ticket className="h-8 w-8 text-success" />
              </div>
              <h2 className="font-display font-bold text-xl mb-2">Voucher Redeemed!</h2>
              <p className="text-muted-foreground mb-6">Show this to the merchant</p>

              {/* QR Code Placeholder */}
              <div className="qr-container mx-auto w-48 h-48 mb-4">
                <div className="w-full h-full bg-muted rounded-lg flex items-center justify-center">
                  <span className="text-muted-foreground text-sm">QR Code</span>
                </div>
              </div>

              {/* Voucher Code */}
              <div className="bg-muted rounded-lg p-4 mb-4">
                <p className="text-xs text-muted-foreground mb-1">Voucher Code</p>
                <p className="font-mono font-bold text-lg">PHYSIO-{offer.id.toUpperCase()}</p>
              </div>

              <div className="text-sm text-muted-foreground">
                <p>{offer.partnerName}</p>
                <p>{offer.title}</p>
                <p className="mt-2">Valid until: {new Date(offer.expiryDate).toLocaleDateString()}</p>
              </div>
            </CardContent>
          </Card>

          <Button className="w-full" variant="outline" onClick={onBack}>
            Back to Offers
          </Button>
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MobileHeader title="Offer Details" showBack onBack={onBack} />
      <div className="p-4 space-y-6">
        {/* Offer Header */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-muted rounded-xl flex items-center justify-center">
                <Gift className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">{offer.partnerName}</p>
                <h2 className="font-display font-bold text-xl">{offer.title}</h2>
              </div>
            </div>

            <p className="text-muted-foreground mb-4">{offer.description}</p>

            <div className="flex items-center gap-4 mb-4">
              <Badge variant="points" className="text-base px-3 py-1">
                {offer.pointsCost.toLocaleString()} pts
              </Badge>
              <span className="text-muted-foreground">Worth {offer.originalPrice}</span>
            </div>

            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <Badge variant={offer.redemptionType === 'qr' ? 'info' : 'secondary'}>
                {offer.redemptionType === 'qr' ? 'QR Scan' : 'Voucher Code'}
              </Badge>
              <span>Expires: {new Date(offer.expiryDate).toLocaleDateString()}</span>
            </div>
          </CardContent>
        </Card>

        {/* T&C */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Terms & Conditions</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>• Valid at all {offer.partnerName} outlets</p>
            <p>• Cannot be combined with other promotions</p>
            <p>• Single use only</p>
            <p>• Non-transferable and non-refundable</p>
            <p>• Subject to availability</p>
          </CardContent>
        </Card>

        {/* Redeem Button */}
        <div className="space-y-3">
          <Button
            className="w-full"
            size="lg"
            onClick={handleRedeem}
            disabled={isRedeeming || userWallet.availablePoints < offer.pointsCost}
          >
            {isRedeeming ? 'Processing...' : `Redeem for ${offer.pointsCost.toLocaleString()} pts`}
          </Button>
          {userWallet.availablePoints < offer.pointsCost && (
            <p className="text-center text-sm text-destructive">
              Insufficient points. You need {(offer.pointsCost - userWallet.availablePoints).toLocaleString()} more points.
            </p>
          )}
        </div>
      </div>
      <MobileBottomNav />
    </div>
  );
}

// Vouchers Page
export function CustomerVouchers() {
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [filter, setFilter] = useState('active');

  const filteredVouchers = userVouchers.filter((v) => {
    if (filter === 'all') return true;
    return v.status === filter;
  });

  if (selectedVoucher) {
    return (
      <VoucherDetailView
        voucher={selectedVoucher}
        onBack={() => setSelectedVoucher(null)}
      />
    );
  }

  return (
    <div className="pb-24">
      <MobileHeader title="My Vouchers" />
      <div className="p-4 space-y-4">
        {/* Filter Tabs */}
        <div className="flex gap-2">
          {['active', 'used', 'expired', 'all'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>

        {/* Voucher List */}
        <div className="space-y-3">
          {filteredVouchers.length > 0 ? (
            filteredVouchers.map((voucher) => (
              <VoucherCard
                key={voucher.id}
                voucher={voucher}
                onClick={() => setSelectedVoucher(voucher)}
              />
            ))
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Ticket className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No {filter} vouchers</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <MobileBottomNav />
    </div>
  );
}

// Voucher Detail View
function VoucherDetailView({ voucher, onBack }) {
  return (
    <div className="min-h-screen bg-background">
      <MobileHeader title="Voucher Details" showBack onBack={onBack} />
      <div className="p-4 space-y-6">
        <Card className="text-center">
          <CardContent className="p-6">
            <Badge
              variant={voucher.status === 'active' ? 'active' : voucher.status === 'used' ? 'secondary' : 'expired'}
              className="mb-4"
            >
              {voucher.status.charAt(0).toUpperCase() + voucher.status.slice(1)}
            </Badge>

            <h2 className="font-display font-bold text-xl mb-2">{voucher.title}</h2>
            <p className="text-muted-foreground mb-6">{voucher.partnerName}</p>

            {voucher.status === 'active' && (
              <>
                {/* QR Code */}
                <div className="qr-container mx-auto w-48 h-48 mb-4">
                  <div className="w-full h-full bg-muted rounded-lg flex items-center justify-center">
                    <span className="text-muted-foreground text-sm">QR Code</span>
                  </div>
                </div>

                {/* Voucher Code */}
                <div className="bg-muted rounded-lg p-4 mb-4">
                  <p className="text-xs text-muted-foreground mb-1">Voucher Code</p>
                  <p className="font-mono font-bold text-lg">{voucher.voucherCode}</p>
                </div>

                <p className="text-sm text-muted-foreground">
                  Show this to the merchant to redeem
                </p>
              </>
            )}

            {voucher.status === 'used' && (
              <div className="text-muted-foreground">
                <p>Used on {voucher.usedDate}</p>
              </div>
            )}

            {voucher.status === 'expired' && (
              <div className="text-muted-foreground">
                <p>Expired on {voucher.expiryDate}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Redeemed on</span>
              <span className="font-medium">{voucher.redeemedDate}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Points used</span>
              <span className="font-medium">{voucher.pointsUsed.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Expires on</span>
              <span className="font-medium">{voucher.expiryDate}</span>
            </div>
          </CardContent>
        </Card>

        <Button variant="outline" className="w-full" onClick={onBack}>
          Back to Vouchers
        </Button>
      </div>
      <MobileBottomNav />
    </div>
  );
}

// History Page
export function CustomerHistory() {
  const [filter, setFilter] = useState('all');

  const filteredTransactions = transactions.filter((t) => {
    if (filter === 'all') return true;
    return t.type === filter;
  });

  return (
    <div className="pb-24">
      <MobileHeader title="Transaction History" />
      <div className="p-4 space-y-4">
        {/* Filter Tabs */}
        <div className="flex gap-2">
          {['all', 'earn', 'redeem'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>

        {/* Transaction List */}
        <Card>
          <CardContent className="p-0 divide-y divide-border">
            {filteredTransactions.map((transaction) => (
              <TransactionRow key={transaction.id} transaction={transaction} />
            ))}
          </CardContent>
        </Card>
      </div>
      <MobileBottomNav />
    </div>
  );
}
